import {
  ArrowBigUp,
  Camera,
  CloudOff,
  Ellipsis,
  ExternalLink,
  Info,
  ListChecks,
  Pencil,
  Plus,
  Radar,
  Search,
  Send,
  Trash,
} from 'lucide-react'
import { useMemo, type FormEvent } from 'react'
import { useViewState } from '../lib/navigation'
import { useViewDraft } from '../lib/useViewDraft'
import { Link } from 'react-router'
import { isApiError } from '../api/client'
import {
  useCollections,
  useCreateCollection,
  useDeleteCollection,
  usePauseSource,
  useRenameCollection,
  useResumeSource,
  useSetCollectionSources,
  useSources,
} from '../api/queries'
import type { Collection, Platform, Source, SourceStatus } from '../api/types'
import { Avatar } from '../components/Avatar'
import { Button, buttonClass } from '../components/Button'
import { Dialog } from '../components/Dialog'
import { EmptyState } from '../components/EmptyState'
import { PageHeader } from '../components/PageHeader'
import { Card, Section } from '../components/Section'
import { Chip } from '../components/Select'
import { Spinner } from '../components/Spinner'
import { toast } from '../components/Toast'
import { AddSourceDialog } from '../components/sources/AddSourceDialog'
import { openExternal } from '../lib/download'
import {
  PLATFORM_LABEL,
  handleLabel,
  sourceInput,
} from '../lib/format'
import { OwnTag, RemoveDialog, SourceDialog, StateTag, StatusBadge, StatusDot, type SourceAction } from '../components/sources/SourceDetails'

type StatusFilter = 'all' | SourceStatus
type PlatformFilter = 'all' | Platform

const PLATFORM_CHIPS: PlatformFilter[] = ['all', 'instagram', 'telegram', 'reddit']

const STATUS_CHIPS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'Все' },
  { value: 'found', label: 'Есть находки' },
  { value: 'quiet', label: 'Тишина' },
  { value: 'pending', label: 'Ждёт обхода' },
  { value: 'error', label: 'Ошибка' },
]

type DialogState =
  | { kind: 'create' }
  | { kind: 'rename'; collection: Collection }
  | { kind: 'delete'; collection: Collection }
  | { kind: 'assign'; collection: Collection }
  | { kind: 'add'; input?: string }
  | { kind: 'source'; source: Source }
  | { kind: 'remove'; source: Source }

type DialogRef = { kind: DialogState['kind']; id?: number; input?: string }

export function SourcesPage() {
  const sources = useSources({ includeRemoved: true })
  const collections = useCollections()
  const [query, setQuery] = useViewState('sources.query', '')
  const [status, setStatus] = useViewState<StatusFilter>('sources.status', 'all')
  const [platform, setPlatform] = useViewState<PlatformFilter>('sources.platform', 'all')
  const [showRemoved, setShowRemoved] = useViewState('sources.removed', false)
  const [dialogRef, setDialogRef] = useViewState<DialogRef | null>('sources.dialog', null)
  const setDialog = (value: DialogState | null) => setDialogRef(value === null ? null : {
    kind: value.kind,
    ...('collection' in value ? { id: value.collection.id } : 'source' in value ? { id: value.source.id } : {}),
    ...(value.kind === 'add' ? { input: value.input } : {}),
  })
  // Храним только идентификатор окна; карточку берём из актуального query-кэша.
  const dialog: DialogState | null = (() => {
    if (!dialogRef) return null
    if (dialogRef.kind === 'create') return { kind: 'create' }
    if (dialogRef.kind === 'add') return { kind: 'add', input: dialogRef.input }
    if (dialogRef.kind === 'source' || dialogRef.kind === 'remove') {
      const source = sources.data?.find((item) => item.id === dialogRef.id)
      return source ? { kind: dialogRef.kind, source } : null
    }
    const collection = collections.data?.find((item) => item.id === dialogRef.id)
    return collection ? { kind: dialogRef.kind, collection } : null
  })()
  const pause = usePauseSource()
  const resume = useResumeSource()

  const live = useMemo(() => (sources.data ?? []).filter((s) => s.state !== 'removed'), [sources.data])
  const removedCount = (sources.data?.length ?? 0) - live.length
  const listed = useMemo(
    () => (showRemoved ? (sources.data ?? []) : live),
    [showRemoved, sources.data, live],
  )

  const matched = useMemo(() => {
    const q = query.trim().replace(/^[@/]|^r\//i, '').toLowerCase()
    return listed.filter((s) => !q || s.handle.toLowerCase().includes(q) || (s.title ?? '').toLowerCase().includes(q))
  }, [listed, query])
  // Числа на чипах площадок — с учётом поиска, на чипах статусов — ещё и площадки.
  const platformCounts = useMemo(() => {
    const c: Record<PlatformFilter, number> = { all: matched.length, instagram: 0, telegram: 0, reddit: 0 }
    for (const s of matched) c[s.platform] += 1
    return c
  }, [matched])
  // Площадки без источников не показываем: Reddit появится с первым сабреддитом.
  const platformChips = useMemo(
    () => PLATFORM_CHIPS.filter((p) => p === 'all' || p === platform || listed.some((s) => s.platform === p)),
    [listed, platform],
  )
  const searched = useMemo(
    () => matched.filter((s) => platform === 'all' || s.platform === platform),
    [matched, platform],
  )
  const counts = useMemo(() => {
    const c: Record<StatusFilter, number> = { all: searched.length, found: 0, quiet: 0, error: 0, pending: 0 }
    for (const s of searched) c[s.status] += 1
    return c
  }, [searched])
  // «Ждёт обхода» бывает только у новых источников: без них чипа нет.
  const statusChips = STATUS_CHIPS.filter((c) => c.value !== 'pending' || counts.pending > 0 || status === 'pending')
  const visible = searched.filter((s) => status === 'all' || s.status === status)
  const filtering = query.trim() !== '' || status !== 'all' || platform !== 'all'

  const groups = useMemo(() => {
    const list = collections.data ?? []
    const kept = visible.filter((s) => s.state !== 'removed')
    const gone = visible.filter((s) => s.state === 'removed')
    const removedGroup = { key: 'removed', collection: null, title: 'Убранные' as string | null, items: gone }
    if (list.length === 0) {
      return [{ key: 'all', collection: null, title: null as string | null, items: kept }, removedGroup]
    }
    return [
      ...list.map((c) => ({
        key: String(c.id),
        collection: c,
        title: c.name as string | null,
        items: kept.filter((s) => c.source_ids.includes(s.id)),
      })),
      {
        key: 'none',
        collection: null,
        title: 'Без подборки' as string | null,
        items: kept.filter((s) => s.collection_ids.length === 0),
      },
      removedGroup,
    ]
  }, [collections.data, visible])

  const act = (action: SourceAction, s: Source) => {
    const name = handleLabel(s.platform, s.handle)
    if (action === 'details') setDialog({ kind: 'source', source: s })
    else if (action === 'remove') setDialog({ kind: 'remove', source: s })
    // Убранный возвращается через карточку добавления: цена и подборки видны заново.
    else if (action === 'restore') setDialog({ kind: 'add', input: sourceInput(s.platform, s.handle) })
    else if (action === 'pause') {
      pause.mutate(s.id, {
        onSuccess: () => toast(`${name} на паузе`),
        onError: () => toast('Не получилось поставить на паузу', { error: true }),
      })
    } else {
      resume.mutate(s.id, {
        onSuccess: () => toast(`${name} снова собирается`),
        onError: (err) =>
          toast(
            isApiError(err, 409) && err.detail === 'limit_reached'
              ? `Потолок активных ${PLATFORM_LABEL[s.platform]}: поставьте кого-то на паузу`
              : isApiError(err, 409) && err.detail === 'reddit_off'
                ? 'Reddit выключен: нет ключей Reddit API'
                : 'Не получилось вернуть',
            { error: true, ms: 4000 },
          ),
      })
    }
  }
  const busyId = pause.isPending ? pause.variables : resume.isPending ? resume.variables : null

  return (
    <>
      <PageHeader
        title="Источники"
        description="Аккаунты конкурентов и подборки по нишам."
        actions={
          <>
            <Button size="sm" onClick={() => setDialog({ kind: 'create' })}>
              <Plus size={14} />
              Подборка
            </Button>
            <Button variant="primary" size="sm" onClick={() => setDialog({ kind: 'add' })}>
              <Plus size={14} />
              Источник
            </Button>
          </>
        }
      />

      {sources.isPending ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : sources.isError ? (
        <EmptyState icon={CloudOff} title="Не удалось загрузить источники">
          <Button className="mt-3" onClick={() => void sources.refetch()}>
            Повторить
          </Button>
        </EmptyState>
      ) : live.length === 0 && removedCount === 0 ? (
        <EmptyState icon={Radar} title="Источников пока нет">
          <Button variant="primary" className="mt-3" onClick={() => setDialog({ kind: 'add' })}>
            <Plus size={14} />
            Добавить источник
          </Button>
        </EmptyState>
      ) : (
        <>
          <div className="mb-4 flex flex-col gap-2.5 desk:flex-row desk:flex-wrap desk:items-center desk:gap-x-4">
            <label className="relative block desk:w-64">
              <span className="sr-only">Поиск по нику</span>
              <Search size={15} className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-muted" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Поиск по нику"
                className="h-11 w-full rounded-lg bg-card pr-3 pl-9 text-[15px] ring-1 ring-line outline-none ring-inset placeholder:text-muted focus:ring-fg/40 desk:h-8 desk:text-[13px]"
              />
            </label>
            <div className="-mx-[var(--gutter)] flex gap-2 overflow-x-auto px-[var(--gutter)] [scrollbar-width:none] desk:mx-0 desk:px-0" aria-label="Площадка">
              {platformChips.map((p) => {
                const Icon = p === 'all' ? null : PLATFORM_ICON[p]
                return (
                  <Chip key={p} active={platform === p} onClick={() => setPlatform(p)}>
                    {Icon && <Icon size={14} />}
                    {p === 'all' ? 'Все площадки' : PLATFORM_LABEL[p]}
                    <span className="opacity-60 tabular-nums">{platformCounts[p]}</span>
                  </Chip>
                )
              })}
            </div>
            <div className="-mx-[var(--gutter)] flex gap-2 overflow-x-auto px-[var(--gutter)] [scrollbar-width:none] desk:mx-0 desk:px-0" aria-label="Статус">
              {statusChips.map((c) => (
                <Chip key={c.value} active={status === c.value} onClick={() => setStatus(c.value)}>
                  {c.value !== 'all' && <StatusDot status={c.value} />}
                  {c.label}
                  <span className="opacity-60 tabular-nums">{counts[c.value]}</span>
                </Chip>
              ))}
            </div>
          </div>

          {(collections.data?.length ?? 0) === 0 && !filtering && (
            <div className="mb-4 flex items-start gap-2.5 rounded-xl bg-surface p-3.5 text-sm ring-1 ring-line ring-inset">
              <Info size={16} className="mt-0.5 shrink-0 text-muted" />
              <p className="text-pretty">
                Соберите источники в подборки по нишам — в ленте подборка выбирается одним нажатием.{' '}
                <button type="button" className="font-medium text-link hover:underline" onClick={() => setDialog({ kind: 'create' })}>
                  Создать подборку
                </button>
              </p>
            </div>
          )}

          <div className="flex flex-col gap-6">
            {groups
              .filter((g) => g.items.length > 0 || (!filtering && g.collection))
              .map((g) => (
                <Section
                  key={g.key}
                  title={g.title ? `${g.title} · ${g.items.length}` : `Все источники · ${g.items.length}`}
                  aside={
                    g.collection && (
                      <CollectionActions collection={g.collection} onAction={(kind, c) => setDialog({ kind, collection: c })} />
                    )
                  }
                >
                  {g.items.length === 0 ? (
                    <Card className="p-4 text-sm text-muted">
                      В подборке пока нет источников.{' '}
                      <button
                        type="button"
                        className="font-medium text-link hover:underline"
                        onClick={() => g.collection && setDialog({ kind: 'assign', collection: g.collection })}
                      >
                        Выбрать
                      </button>
                    </Card>
                  ) : (
                    <ul className="grid grid-cols-1 divide-y divide-line rounded-xl bg-card ring-1 ring-line desk:gap-3 desk:divide-y-0 desk:bg-transparent desk:ring-0 desk:lg:grid-cols-2 desk:xl:grid-cols-3">
                        {g.items.map((s) => (
                          <SourceRow key={s.id} source={s} busy={busyId === s.id} onAction={act} />
                        ))}
                    </ul>
                  )}
                </Section>
              ))}
            {filtering && visible.length === 0 && (
              <EmptyState icon={Search} title="Ничего не нашлось">
                Попробуйте другой ник, площадку или статус.
              </EmptyState>
            )}
          </div>

          <div className="mt-6 flex flex-col gap-2 text-xs text-pretty text-muted">
            <p>
              Instagram собирается через Apify и стоит денег, цена видна до добавления. Telegram и Reddit бесплатны.
              Добавлять и ставить на паузу может любой в команде, убирать — только владелец. «Ошибка» — источник
              недоступен или три обхода подряд закончились ошибкой.
            </p>
            {removedCount > 0 && (
              <p>
                <button type="button" className="font-medium text-link hover:underline" onClick={() => setShowRemoved((v) => !v)}>
                  {showRemoved ? 'Скрыть убранные' : `Показать убранные · ${removedCount}`}
                </button>
              </p>
            )}
          </div>
        </>
      )}

      {dialog?.kind === 'create' && <NameDialog onClose={() => setDialog(null)} />}
      {dialog?.kind === 'rename' && <NameDialog collection={dialog.collection} onClose={() => setDialog(null)} />}
      {dialog?.kind === 'delete' && <DeleteDialog collection={dialog.collection} onClose={() => setDialog(null)} />}
      {dialog?.kind === 'assign' && (
        <AssignDialog collection={dialog.collection} sources={live} onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === 'add' && <AddSourceDialog initialInput={dialog.input} onClose={() => setDialog(null)} />}
      {dialog?.kind === 'source' && (
        <SourceDialog
          source={dialog.source}
          onAction={(action, s) => {
            setDialog(null)
            act(action, s)
          }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'remove' && <RemoveDialog source={dialog.source} onClose={() => setDialog(null)} />}
    </>
  )
}

function CollectionActions({
  collection,
  onAction,
}: {
  collection: Collection
  onAction: (kind: 'rename' | 'delete' | 'assign', c: Collection) => void
}) {
  return (
    <div className="-mr-1.5 flex items-center">
      <Button size="sm" variant="ghost" onClick={() => onAction('assign', collection)}>
        <ListChecks size={14} />
        Источники
      </Button>
      <Button size="icon" variant="ghost" aria-label="Переименовать" title="Переименовать" onClick={() => onAction('rename', collection)}>
        <Pencil size={15} />
      </Button>
      <Button size="icon" variant="ghost" aria-label="Удалить подборку" title="Удалить" onClick={() => onAction('delete', collection)}>
        <Trash size={15} />
      </Button>
    </div>
  )
}

const PLATFORM_ICON = { instagram: Camera, telegram: Send, reddit: ArrowBigUp } as const


function SourceRow({
  source: s,
  busy,
  onAction,
}: {
  source: Source
  busy: boolean
  onAction: (action: SourceAction, s: Source) => void
}) {
  const Icon = PLATFORM_ICON[s.platform]
  const name = handleLabel(s.platform, s.handle)
  return (
    <li className="flex min-w-0 flex-col gap-2 p-3 desk:gap-3 desk:rounded-xl desk:bg-card desk:p-4 desk:ring-1 desk:ring-line desk:ring-inset">
      <Link to={`/sources/${s.id}`} className="flex min-w-0 items-center gap-3 rounded-lg outline-offset-4" aria-label={`Карточка источника ${name}`}>
        <Avatar key={s.avatar_url} name={s.title ?? s.handle} src={s.avatar_url ?? null} size={48} />
        <span className="min-w-0 flex-1 leading-tight">
          <span className="flex items-center gap-1.5">
            <span className="truncate font-medium">{name}</span>
            <Icon size={13} className="shrink-0 text-muted" aria-label={PLATFORM_LABEL[s.platform]} />
          </span>
          {s.title && <span className="mt-1 block truncate text-sm text-muted">{s.title}</span>}
          <span className="mt-1 flex flex-wrap gap-1">
            <StateTag source={s} />
            {s.own_board_id != null && <OwnTag boardId={s.own_board_id} />}
          </span>
        </span>
      </Link>
      <div className="flex min-h-5 flex-wrap items-center justify-between gap-2">
        {s.state !== 'removed' && <StatusBadge source={s} />}
        <span className="ml-auto text-xs text-muted tabular-nums">{s.followers === null ? 'Подписчики —' : `${s.followers.toLocaleString('ru-RU')} подп.`}</span>
      </div>
      <div className="mt-auto flex items-center gap-1.5 desk:border-t desk:border-line desk:pt-3">
        <Link to={`/sources/${s.id}`} className={buttonClass('secondary', 'sm', 'min-w-0 flex-1 px-2')}>Собранный контент</Link>
        {s.url && <Button size="icon" variant="ghost" aria-label={`Открыть профиль ${name} в ${PLATFORM_LABEL[s.platform]}`} title="Открыть профиль" onClick={() => openExternal(s.url ?? '')}><ExternalLink size={16} /></Button>}
        <Button size="icon" variant="ghost" aria-label={`Действия с ${name}`} onClick={() => onAction('details', s)}>
          {busy ? <Spinner size={16} /> : <Ellipsis size={18} />}
        </Button>
      </div>
    </li>
  )
}


function NameDialog({ collection, onClose: finishClose }: { collection?: Collection; onClose: () => void }) {
  const create = useCreateCollection()
  const rename = useRenameCollection()
  const [name, setName, resetName] = useViewDraft(`sources.collection.${collection?.id ?? 'new'}.name`, collection?.name ?? '')
  const onClose = () => { resetName(); finishClose() }
  const pending = create.isPending || rename.isPending
  const error = create.error ?? rename.error

  const submit = (e: FormEvent) => {
    e.preventDefault()
    const value = name.trim()
    if (!value) return
    const done = { onSuccess: onClose }
    if (collection) rename.mutate({ id: collection.id, name: value }, done)
    else create.mutate(value, done)
  }

  return (
    <Dialog
      title={collection ? 'Переименовать подборку' : 'Новая подборка'}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" type="submit" form="collection-name" disabled={pending || !name.trim()}>
            {pending && <Spinner size={14} className="text-accent-fg" />}
            {collection ? 'Сохранить' : 'Создать'}
          </Button>
        </>
      }
    >
      <form id="collection-name" onSubmit={submit}>
        <label className="mb-1 block text-xs text-muted" htmlFor="collection-name-input">
          Название
        </label>
        <input
          id="collection-name-input"
          maxLength={60}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Например, ИИ и нейросети"
          className="h-11 w-full rounded-lg bg-surface px-3 text-[15px] ring-1 ring-line outline-none ring-inset focus:ring-fg/40 desk:h-9 desk:text-sm"
        />
        {error && (
          <p className="mt-2 text-sm text-danger">
            {isApiError(error, 409) ? 'Подборка с таким названием уже есть.' : 'Не получилось сохранить.'}
          </p>
        )}
      </form>
    </Dialog>
  )
}

function DeleteDialog({ collection, onClose }: { collection: Collection; onClose: () => void }) {
  const remove = useDeleteCollection()
  return (
    <Dialog
      title="Удалить подборку?"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="danger" disabled={remove.isPending} onClick={() => remove.mutate(collection.id, { onSuccess: onClose })}>
            {remove.isPending && <Spinner size={14} className="text-white" />}
            Удалить
          </Button>
        </>
      }
    >
      <p className="text-sm text-pretty">
        Подборка «{collection.name}» исчезнет из ленты. Источники и их посты останутся.
      </p>
      {remove.isError && <p className="mt-2 text-sm text-danger">Не получилось удалить.</p>}
    </Dialog>
  )
}

function AssignDialog({ collection, sources, onClose: finishClose }: { collection: Collection; sources: Source[]; onClose: () => void }) {
  const save = useSetCollectionSources()
  const [picked, setPicked, resetPicked] = useViewDraft(`sources.collection.${collection.id}.picked`, collection.source_ids)
  const [query, setQuery, resetQuery] = useViewDraft(`sources.collection.${collection.id}.query`, '')
  const onClose = () => { resetPicked(); resetQuery(); finishClose() }
  const q = query.trim().replace(/^@/, '').toLowerCase()
  const list = sources
    .filter((s) => !q || s.handle.toLowerCase().includes(q) || (s.title ?? '').toLowerCase().includes(q))
    .sort((a, b) => a.platform.localeCompare(b.platform) || a.handle.localeCompare(b.handle))

  const toggle = (id: number) =>
    setPicked((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return [...next]
    })

  return (
    <Dialog
      wide
      title={`Источники · ${collection.name}`}
      onClose={onClose}
      footer={
        <>
          <span className="mr-auto self-center text-sm text-muted tabular-nums">
            Выбрано {picked.length}
          </span>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            disabled={save.isPending}
            onClick={() => save.mutate({ id: collection.id, sourceIds: [...picked] }, { onSuccess: onClose })}
          >
            {save.isPending && <Spinner size={14} className="text-accent-fg" />}
            Сохранить
          </Button>
        </>
      }
    >
      <label className="relative mb-2 block">
        <span className="sr-only">Поиск по нику</span>
        <Search size={15} className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-muted" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Поиск по нику"
          className="h-11 w-full rounded-lg bg-surface pr-3 pl-9 text-[15px] ring-1 ring-line outline-none ring-inset focus:ring-fg/40 desk:h-8 desk:text-[13px]"
        />
      </label>
      <ul className="-mx-1">
        {list.map((s) => (
          <li key={s.id}>
            <label className="flex min-h-11 cursor-pointer items-center gap-3 rounded-lg px-1 py-1.5 hover:bg-surface desk:min-h-9">
              <input
                type="checkbox"
                checked={picked.includes(s.id)}
                onChange={() => toggle(s.id)}
                className="size-4 shrink-0 accent-[var(--accent)]"
              />
              <span className="min-w-0 flex-1 truncate text-sm">
                <span className="font-medium">{handleLabel(s.platform, s.handle)}</span>
                <span className="text-muted">
                  {' '}
                  · {PLATFORM_LABEL[s.platform]}
                  {s.title ? ` · ${s.title}` : ''}
                </span>
              </span>
            </label>
          </li>
        ))}
      </ul>
      {save.isError && <p className="mt-2 text-sm text-danger">Не получилось сохранить.</p>}
    </Dialog>
  )
}
